import {
  CaseCreation,
  CaseDefendantLink,
  CountDefendantsList,
  Exhibit,
  Suspect,
  Witness,
} from "types";
import { gregorianToEthiopic } from "ethiopian-calendar-finova";

/**
 * Builds a charge sheet in the layout used before Ethiopian courts (Criminal
 * Procedure Code, Arts. 111-112): court heading, parties, one numbered count
 * per offence with the articles of law and the particulars of the offence,
 * the list of evidence, the request to the court and the prosecutor's
 * signature. Everything is filled from what was registered on the charge and
 * stays editable; empty items are left as [ ... ] for the prosecutor.
 */
export type SheetLanguage = "en" | "am" | "or";

type Labels = {
  title: string;
  court: string;
  courtPlaceholder: string;
  fileNo: string;
  prosecutionNo: string;
  plaintiff: string;
  plaintiffValue: string;
  defendants: string;
  defendantOrdinal: (n: number) => string;
  sex: string;
  age: string;
  address: string;
  inCustody: string;
  notInCustody: string;
  charge: string;
  count: (n: number) => string;
  against: string;
  offence: string;
  articles: string;
  articlesSuffix: string;
  level: string;
  particulars: string;
  alternative: string;
  evidence: string;
  witnesses: string;
  testifiesAgainst: string;
  exhibits: string;
  quantity: string;
  none: string;
  request: string;
  prosecutor: string;
  signature: string;
  date: string;
  levels: Record<string, string>;
};

const ordinalEn = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export const LABELS: Record<SheetLanguage, Labels> = {
  en: {
    title: "CRIMINAL CHARGE",
    court: "To the",
    courtPlaceholder: "[ Name of the court ]",
    fileNo: "Court file no.",
    prosecutionNo: "Prosecution reg. no.",
    plaintiff: "Plaintiff",
    plaintiffValue: "The Public Prosecutor",
    defendants: "Defendants",
    defendantOrdinal: (n) => `${ordinalEn(n)} defendant`,
    sex: "Sex",
    age: "Age",
    address: "Address",
    inCustody: "in custody",
    notInCustody: "not in custody",
    charge: "CHARGE",
    count: (n) => `${ordinalEn(n)} count`,
    against: "Against",
    offence: "Offence",
    articles: "Contrary to",
    articlesSuffix: "of the Criminal Code of the FDRE (Proclamation No. 414/2004)",
    level: "Gravity",
    particulars: "Particulars of the offence",
    alternative: "In the alternative",
    evidence: "EVIDENCE",
    witnesses: "A. Witnesses",
    testifiesAgainst: "testifies against",
    exhibits: "B. Exhibits and documentary evidence",
    quantity: "Quantity",
    none: "None registered",
    request:
      "The public prosecutor hereby submits this charge and respectfully requests the Honourable Court to summon the defendants, hear the evidence listed above and decide the case in accordance with the law.",
    prosecutor: "Public prosecutor",
    signature: "Signature",
    date: "Date",
    levels: { Heavy: "Serious", Medium: "Medium", Light: "Minor" },
  },
  am: {
    title: "የወንጀል ክስ",
    court: "ለ",
    courtPlaceholder: "[ የፍርድ ቤቱ ስም ]",
    fileNo: "የመዝገብ ቁጥር",
    prosecutionNo: "የዐቃቤ ሕግ መዝገብ ቁጥር",
    plaintiff: "ከሳሽ",
    plaintiffValue: "የሕዝብ ዐቃቤ ሕግ",
    defendants: "ተከሳሾች",
    defendantOrdinal: (n) => `${n}ኛ ተከሳሽ`,
    sex: "ጾታ",
    age: "ዕድሜ",
    address: "አድራሻ",
    inCustody: "በማረፊያ ቤት የሚገኝ",
    notInCustody: "በማረፊያ ቤት የማይገኝ",
    charge: "ክስ",
    count: (n) => `${n}ኛ ክስ`,
    against: "ተከሳሽ(ዎች)",
    offence: "ወንጀሉ",
    articles: "የተጣሰው ድንጋጌ",
    articlesSuffix: "የኢ.ፌ.ዴ.ሪ የወንጀል ሕግ (አዋጅ ቁጥር 414/1996)",
    level: "የወንጀሉ ክብደት",
    particulars: "የወንጀሉ ዝርዝር",
    alternative: "በአማራጭ",
    evidence: "ማስረጃዎች",
    witnesses: "ሀ. የሰው ማስረጃ",
    testifiesAgainst: "የሚመሰክሩት በ",
    exhibits: "ለ. ኤግዚቢትና የሰነድ ማስረጃ",
    quantity: "ብዛት",
    none: "አልተመዘገበም",
    request:
      "ዐቃቤ ሕግ ይህንን ክስ ያቀረበ ሲሆን የተከበረው ፍርድ ቤት ተከሳሾችን አስቀርቦ ከላይ የተዘረዘሩትን ማስረጃዎች በመስማት በሕጉ መሠረት እንዲወስን በአክብሮት ይጠይቃል።",
    prosecutor: "ዐቃቤ ሕግ",
    signature: "ፊርማ",
    date: "ቀን",
    levels: { Heavy: "ከባድ", Medium: "መካከለኛ", Light: "ቀላል" },
  },
  or: {
    title: "HIMANNAA YAKKAA",
    court: "Gara",
    courtPlaceholder: "[ Maqaa mana murtii ]",
    fileNo: "Lakk. galmee mana murtii",
    prosecutionNo: "Lakk. galmee abbaa alangaa",
    plaintiff: "Himataa",
    plaintiffValue: "Abbaa Alangaa",
    defendants: "Himatamtoota",
    defendantOrdinal: (n) => `Himatamaa ${n}ffaa`,
    sex: "Saala",
    age: "Umurii",
    address: "Teessoo",
    inCustody: "too'annoo jala kan jiru",
    notInCustody: "too'annoo jala kan hin jirre",
    charge: "HIMANNAA",
    count: (n) => `Himannaa ${n}ffaa`,
    against: "Himatamtoota",
    offence: "Yakka",
    articles: "Labsii cabe",
    articlesSuffix: "Seera Yakkaa RFDI (Labsii Lakk. 414/1996)",
    level: "Ulfaatina yakkaa",
    particulars: "Ibsa yakkaa",
    alternative: "Filannoon",
    evidence: "RAGAALEE",
    witnesses: "A. Ragaa namaa",
    testifiesAgainst: "irratti kan ragaa ba'u",
    exhibits: "B. Meeshaa ragaa fi ragaa barreeffamaa",
    quantity: "Baay'ina",
    none: "Hin galmeeffamne",
    request:
      "Abbaan alangaa himannaa kana dhiyeessee jira; Manni Murtii kabajamaan himatamtoota dhiyeessee ragaalee armaan olii dhaggeeffachuun akka seeraatti akka murteessu kabajaan gaafata.",
    prosecutor: "Abbaa alangaa",
    signature: "Mallattoo",
    date: "Guyyaa",
    levels: { Heavy: "Cimaa", Medium: "Giddugaleessa", Light: "Salphaa" },
  },
};

// Today in the Ethiopian calendar as plain text (the app's DateView returns JSX).
const todayEC = () => {
  const e = gregorianToEthiopic(new Date());
  return `${e.day}/${e.month}/${e.year} EC`;
};

const escape = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const blank = (value: unknown, placeholder = "[ ... ]") => {
  const text = String(value ?? "").trim();
  return text ? escape(text) : `<span class="cs-blank">${placeholder}</span>`;
};

const personName = (p?: Suspect | Witness | null) =>
  [p?.first_name, p?.middle_name, p?.last_name].filter(Boolean).join(" ");

const personAddress = (p?: Suspect | Witness | null) =>
  [p?.region, p?.zone, p?.city, p?.woreda, p?.kebele && `Kebele ${p.kebele}`, p?.house_number && `H.No. ${p.house_number}`]
    .filter(Boolean)
    .join(", ");

const linksOf = (value: unknown): CaseDefendantLink[] =>
  Array.isArray(value) ? (value.filter((v) => v && typeof v === "object") as CaseDefendantLink[]) : [];

export type ChargeSheetInput = {
  caseData: CaseCreation;
  prosecutorName?: string;
  language: SheetLanguage;
};

export const buildChargeSheet = ({ caseData, prosecutorName, language }: ChargeSheetInput) => {
  const L = LABELS[language] ?? LABELS.en;
  const court = caseData.opening_case_files?.[0];
  const courtName = [court?.court_level, court?.court_type, court?.court_hearing_place]
    .filter(Boolean)
    .join(" ");

  const defendantLinks = [...linksOf(caseData.case_defendant_links)].sort(
    (a, b) => (a.defendant_number || 0) - (b.defendant_number || 0),
  );
  const ordinal = (link: CaseDefendantLink, index: number) =>
    L.defendantOrdinal(link.defendant_number || index + 1);
  const ordinalById = new Map(defendantLinks.map((l, i) => [l.id, ordinal(l, i)]));
  const namesOf = (links: CaseDefendantLink[]) =>
    links.map((l) => ordinalById.get(l.id) || personName(l.defendant_info)).filter(Boolean).join(", ");

  const defendants = defendantLinks.length
    ? defendantLinks
        .map((link, i) => {
          const d = link.defendant_info;
          return `<li><b>${escape(ordinal(link, i))}:</b> ${blank(personName(d))}; ${L.sex}: ${blank(d?.gender)}; ${L.age}: ${blank(d?.age)}; ${L.address}: ${blank(personAddress(d))}${
            d?.is_in_custody === true ? ` (${L.inCustody})` : d?.is_in_custody === false ? ` (${L.notInCustody})` : ""
          }</li>`;
        })
        .join("")
    : `<li>${blank("")}</li>`;

  const counts = [...((caseData.counts || []) as CountDefendantsList[])].sort(
    (a, b) => (a.count_number || 0) - (b.count_number || 0),
  );
  const countBlock = (c: CountDefendantsList, i: number) => {
    const against = namesOf(linksOf(c.defendants_info));
    const level = c.crime_level ? L.levels[c.crime_level] || c.crime_level : "";
    const alternate = c.has_alternate
      ? `<p class="cs-alt"><b>${L.alternative}:</b> ${L.offence}: ${blank(c.alternate_crime_type)}; ${L.articles} ${blank(
          c.alternate_articles_ofthe_charge,
        )} ${L.articlesSuffix}.<br/>${L.particulars}: ${blank(c.alternate_prosecutor_decision_description)}</p>`
      : "";
    return `<section class="cs-count">
      <h3>${escape(L.count(c.count_number || i + 1))}</h3>
      <p><b>${L.against}:</b> ${blank(against)}</p>
      <p><b>${L.offence}:</b> ${blank(c.crime_type)}${c.crime_type_category ? ` (${escape(c.crime_type_category)})` : ""}${
        level ? `; ${L.level}: ${escape(level)}` : ""
      }</p>
      <p><b>${L.articles}:</b> ${blank(c.articles_ofthe_charge)} ${L.articlesSuffix}.</p>
      <p><b>${L.particulars}:</b> ${blank(c.prosecutor_decision_description)}</p>
      ${alternate}
    </section>`;
  };

  const witnesses = (caseData.witnesses || []).length
    ? (caseData.witnesses || [])
        .map((w) => {
          const info = w.witnessed_info;
          const against = namesOf(linksOf(w.witnessed_defendants_info));
          return `<li>${blank(personName(info))}; ${L.address}: ${blank(personAddress(info))}${
            against ? ` — ${L.testifiesAgainst} ${escape(against)}` : ""
          }</li>`;
        })
        .join("")
    : `<li>${L.none}</li>`;

  const exhibits = (caseData.exhibits || []).length
    ? ((caseData.exhibits || []) as Exhibit[])
        .map(
          (e) =>
            `<li>${blank(e.description || e.tool_type)}${e.quantity_written ? `; ${L.quantity}: ${escape(e.quantity_written)}` : ""}${
              e.reference_number ? ` (${escape(e.reference_number)})` : ""
            }</li>`,
        )
        .join("")
    : `<li>${L.none}</li>`;

  return `<header class="cs-head">
  <p class="cs-court">${L.court} ${blank(courtName, L.courtPlaceholder)}</p>
  <table class="cs-refs"><tr>
    <td>${L.fileNo}: ${blank(court?.court_registration_number)}</td>
    <td>${L.prosecutionNo}: ${blank(caseData.prosecutor_registration_number)}</td>
  </tr></table>
</header>
<p><b>${L.plaintiff}:</b> ${L.plaintiffValue}</p>
<p><b>${L.defendants}:</b></p>
<ol class="cs-list">${defendants}</ol>
<h2 class="cs-title">${L.title}</h2>
${counts.length ? counts.map(countBlock).join("") : `<section class="cs-count"><h3>${escape(L.count(1))}</h3><p>${blank("")}</p></section>`}
<h2 class="cs-subtitle">${L.evidence}</h2>
<p><b>${L.witnesses}</b></p>
<ol class="cs-list">${witnesses}</ol>
<p><b>${L.exhibits}</b></p>
<ol class="cs-list">${exhibits}</ol>
<p class="cs-request">${L.request}</p>
<table class="cs-sign"><tr>
  <td>${L.prosecutor}: ${blank(prosecutorName)}</td>
  <td>${L.signature}: ________________</td>
  <td>${L.date}: ${escape(todayEC())}</td>
</tr></table>`;
};

/** Styles shared by the editor, the printout and the Word file. */
export const SHEET_CSS = `
.cs-page{font-family:"Times New Roman","Nyala","Abyssinica SIL",serif;font-size:13pt;line-height:1.55;color:#111}
.cs-page p{margin:0 0 6pt}
.cs-court{text-align:center;font-weight:bold;font-size:14pt;margin-bottom:8pt}
.cs-refs,.cs-sign{width:100%;border-collapse:collapse;margin:6pt 0 12pt}
.cs-refs td,.cs-sign td{padding:2pt 0;vertical-align:top}
.cs-refs td:last-child{text-align:right}
.cs-title{text-align:center;font-size:16pt;letter-spacing:1pt;margin:16pt 0 10pt;text-decoration:underline}
.cs-subtitle{font-size:14pt;margin:14pt 0 6pt;text-decoration:underline}
.cs-count{margin:0 0 10pt}
.cs-count h3{font-size:13pt;margin:10pt 0 4pt;text-decoration:underline}
.cs-alt{margin-left:18pt}
.cs-list{margin:0 0 8pt 18pt;padding:0}
.cs-list li{margin-bottom:3pt}
.cs-request{margin-top:14pt;text-align:justify}
.cs-sign{margin-top:28pt}
.cs-blank{background:#fff3b0;color:#7a5b00}
@media print{.cs-blank{background:none;color:inherit}}
`;

export const fullDocument = (body: string, title: string) => `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${escape(title)}</title>
<style>@page{size:A4;margin:2.2cm 2cm}body{margin:0}${SHEET_CSS}</style></head>
<body><div class="cs-page">${body}</div></body></html>`;
